/**
 * Which pricing arm an ANONYMOUS marketing surface should render.
 *
 * The paywall's quote comes from `GET /billing/checkout-config` instead
 * (server re-validated, trial-eligibility aware, and it fires the exposure
 * event); this resolves what a cached public page DISPLAYS.
 * Nothing here fires an exposure event — `/pricing` is CDN-cached and
 * SEO-indexed, so counting its renders would drown revenue-per-exposed in
 * organic traffic that never sees a checkout (decision C5). Exposure belongs
 * to the paywall.
 *
 * Plan: docs/projects/active/edge-experiments-account-pinned-bucketing.md (M8).
 */
import {
  pricingArmForEdgeCode,
  PRICING_TEST_V2_FLAG_KEY,
  type PricingTestV2ArmKey,
} from '@listening/api-contract/pricing-experiment';
import { useFlag } from '@listening/frontend/analytics/client';
import { useRouteContext } from '@tanstack/react-router';

/**
 * The resolved arm, or `null` when this render has no assignment to show.
 *
 * `null` is not "control" — it is "we were not told", and the caller turns it
 * into the fallback arm via `marketingPricingForArm`. Three real populations
 * land there: crawlers and cookie-less requests (the edge stamps them the
 * fallback code, which decodes to the same arm), and any page the experiment
 * is not registered on (`/audio`, `/edu`, `/newsletter`, `/ref`, `/college`,
 * `/youtube` — see the byte note in `experimental-routes.ts`).
 *
 * **Why the route context and not `useFlag` alone.** `useFlag` collapses "no
 * assignment" into the flag's `default`, which for `pricingTestV2` is `'a'` —
 * control, the $39 arm. Rendering $39 to an unassigned viewer is the
 * advertised-below-charged direction C4 exists to make impossible, so the
 * presence of an assignment has to be read from the thing that actually knows:
 * the `EdgeFlags` map `__root.tsx` resolves per navigation (from `x-variants`
 * at the edge, or the same bucketing primitives in the lambda/client
 * fallback). `useFlag` still supplies the VALUE, so the devtools override keeps
 * working — which is how a price test gets QA'd at all.
 */
export function useMarketingPricingArm(): PricingTestV2ArmKey | null {
  const { value, isOverridden } = useFlag('pricingTestV2');
  const { edgeFlags } = useRouteContext({ from: '__root__' });
  const isAssigned = edgeFlags?.[PRICING_TEST_V2_FLAG_KEY]?.enrolled === true;
  return isOverridden || isAssigned ? pricingArmForEdgeCode(value) : null;
}
